'use client';

/**
 * A control that leans, very slightly, toward the pointer as it approaches.
 *
 * After React Bits' Magnet (MIT + Commons Clause, used inside the product as
 * its licence allows), turned down to a suggestion: a few pixels of pull
 * inside a small reach, capped, and a spring back to rest when the pointer
 * leaves. It marks the one thing on a screen worth reaching for without
 * turning it into a toy. Off for touch (there is no approaching finger) and
 * for reduced motion.
 */

import { useEffect, useRef, type ReactNode } from 'react';

/** How far beyond the element's edge the pull begins, px. */
const REACH_PX = 56;
/** Share of the pointer's offset the element follows. */
const PULL = 0.14;
/** The most it ever moves, px — a lean, not a chase. */
const MAX_PX = 7;

export default function Magnetic({ children, className = '' }: { children: ReactNode; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce), (hover: none)').matches) return;
    let frame = 0;
    let pulled = false;
    const onMove = (e: PointerEvent) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const r = el.getBoundingClientRect();
        const dx = e.clientX - (r.left + r.width / 2);
        const dy = e.clientY - (r.top + r.height / 2);
        const near = Math.abs(dx) < r.width / 2 + REACH_PX && Math.abs(dy) < r.height / 2 + REACH_PX;
        if (near) {
          const x = Math.max(-MAX_PX, Math.min(MAX_PX, dx * PULL));
          const y = Math.max(-MAX_PX, Math.min(MAX_PX, dy * PULL));
          el.style.transition = 'transform 140ms linear';
          el.style.transform = `translate3d(${x}px, ${y}px, 0)`;
          pulled = true;
        } else if (pulled) {
          el.style.transition = 'transform var(--rt-spring-ms) var(--rt-ease-spring)';
          el.style.transform = '';
          pulled = false;
        }
      });
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onMove);
      cancelAnimationFrame(frame);
    };
  }, []);

  return <span ref={ref} className={`inline-flex ${className}`}>{children}</span>;
}
